import { Box, Divider } from "@mui/material";
import React, { useMemo } from "react";
import { v4 as uuidv4 } from "uuid";
import { DataSourcesStore } from "../../stores/DataSourcesStore";
import { observer } from "mobx-react";
import DataSourceContainer from "../../components/DataSourceContainer";
import AddButton from "../../components/AddButton";
import ConditionRule from "./ConditionRule";
import theme from "Theme/AppTheme";
import {
    emptyCondition,
    emptyConditionIndicator,
} from "../../helpers/emptyStates";

type ConditionalLogicProps = {
    store: DataSourcesStore;
};

const ConditionalLogic: React.FC<ConditionalLogicProps> = observer(
    ({ store }) => {
        const {
            sourceFields,
            conditionRules,
            setConditionRules,
            updateSelectedTargetFields,
            getUpdatedTargetFields,
        } = store;

        const addConditionIndicator = (ruleId: string) => {
            setConditionRules(
                conditionRules.map((rule) => {
                    if (rule.id === ruleId) {
                        return {
                            ...rule,
                            conditionIndicators: [
                                ...(rule.conditionIndicators || []),
                                emptyConditionIndicator(),
                            ],
                        };
                    }
                    return rule;
                }),
            );
        };

        const removeConditionIndicator = (
            ruleId: string,
            indicatorId: string,
        ) => {
            setConditionRules(
                conditionRules.map((rule) => {
                    if (rule.id === ruleId) {
                        return {
                            ...rule,
                            conditionIndicators:
                                rule.conditionIndicators.filter(
                                    (indicator) => indicator.id !== indicatorId,
                                ),
                        };
                    }
                    return rule;
                }),
            );
        };

        const updateIndicator = (
            ruleId: string,
            indicatorId: string,
            key: string,
            value: string | number,
        ) => {
            setConditionRules(
                conditionRules.map((rule) => {
                    if (rule.id === ruleId) {
                        return {
                            ...rule,
                            conditionIndicators: rule.conditionIndicators.map(
                                (indicator) => {
                                    if (indicator.id === indicatorId) {
                                        return {
                                            ...indicator,
                                            [key]: value,
                                        };
                                    }
                                    return indicator;
                                },
                            ),
                        };
                    }
                    return rule;
                }),
            );
        };

        const addConditionRule = () => {
            setConditionRules([...conditionRules, emptyCondition()]);
        };

        const removeConditionRule = (id: string) => {
            updateSelectedTargetFields({ sourceFieldId: id, action: "remove" });
            setConditionRules(conditionRules.filter((rule) => rule.id !== id));
        };

        const updateTargetField = (
            ruleId: string,
            targetFieldName: string,
            targetFieldId: string,
        ) => {
            updateSelectedTargetFields({
                sourceFieldId: ruleId,
                targetFieldId,
                action: targetFieldName ? "add" : "remove",
            });

            setConditionRules(
                conditionRules.map((rule) => {
                    if (ruleId === rule.id) {
                        return {
                            ...rule,
                            targetFieldName,
                        };
                    }
                    return rule;
                }),
            );
        };

        const addElseStatement = (ruleId: string) => {
            setConditionRules(
                conditionRules.map((rule) => {
                    if (ruleId === rule.id) {
                        return {
                            ...rule,
                            else: {
                                id: uuidv4(),
                                hasError: false,
                            },
                        };
                    }
                    return rule;
                }),
            );
        };

        const fieldOptions = useMemo(
            () =>
                sourceFields
                    .map((f) => ({
                        label: f.alias,
                        value: f.alias,
                    }))
                    .filter((f) => f.value),
            [sourceFields],
        );

        return (
            <DataSourceContainer
                heading="Conditional Logic"
                subHeading="Define conditions to control when data should be included in your mappings using flexible AND/OR rules"
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                    }}
                >
                    {conditionRules.map((rule, index) => {
                        return (
                            <>
                                <ConditionRule
                                    key={rule.id}
                                    rule={rule}
                                    fieldOptions={fieldOptions}
                                    conditionRules={conditionRules}
                                    setConditionRules={setConditionRules}
                                    getUpdatedTargetFields={
                                        getUpdatedTargetFields
                                    }
                                    updateTargetField={(
                                        value,
                                        targetFieldId,
                                    ) => {
                                        updateTargetField(
                                            rule.id,
                                            value,
                                            targetFieldId,
                                        );
                                    }}
                                    updateIndicator={(
                                        indicatorId,
                                        key,
                                        value,
                                    ) => {
                                        updateIndicator(
                                            rule.id,
                                            indicatorId,
                                            key,
                                            value,
                                        );
                                    }}
                                    removeConditionIndicator={(indicatorId) => {
                                        removeConditionIndicator(
                                            rule.id,
                                            indicatorId,
                                        );
                                    }}
                                    addConditionIndicator={() => {
                                        addConditionIndicator(rule.id);
                                    }}
                                    removeConditionRule={() => {
                                        removeConditionRule(rule.id);
                                    }}
                                    addElseStatement={() => {
                                        addElseStatement(rule.id);
                                    }}
                                />
                                {index !== conditionRules.length - 1 && (
                                    <Divider
                                        sx={{
                                            borderColor:
                                                theme.palette.primary.main,
                                        }}
                                    />
                                )}
                            </>
                        );
                    })}
                </Box>

                <AddButton onClick={addConditionRule} sx={{ marginTop: 2 }}>
                    Condition
                </AddButton>
            </DataSourceContainer>
        );
    },
);

export default ConditionalLogic;
